import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ContainerComponent } from '../components/container/container.component';
import { MessagesListComponent } from '../components/messages-list/messages-list.component';
import { MessagesListItemComponent } from '../components/messages-list/messages-list-item/messages-list-item.component';
import { ScrollContainerContext } from '../components/resizable-split-layout/scroll-container.context';
import { useRequest } from '../hooks/useRequest';
import {
  ChatDetail,
  ChatMessage,
  downloadSharedChatFile,
  fetchSharedChat,
} from '../utils/axios/api';
import { ChatMessagesBuilder } from '../utils/chat-messages.builder';

export function SharedChatPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const loadRequest = useRequest<ChatDetail>();
  const [chatName, setChatName] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  );

  useEffect(() => {
    setChatName('');
    setMessages([]);

    void loadRequest.fetch(() => fetchSharedChat(token)).then((chat) => {
      if (!chat) {
        return;
      }

      setChatName(chat.name);
      setMessages(ChatMessagesBuilder.from(chat.messages).build());
    });
  }, [loadRequest, token]);

  const downloadFile = useCallback(
    (fileId: string) => downloadSharedChatFile(token, fileId),
    [token],
  );

  const error = loadRequest.exception
    ? t(`exceptions.${loadRequest.exception}`)
    : null;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 48,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {chatName || t('sharedChat.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t('sharedChat.readonly')}
        </Typography>
      </Box>
      <ScrollContainerContext.Provider value={scrollContainer}>
        <Box
          ref={setScrollContainer}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <ContainerComponent>
            {!loadRequest.isDone || loadRequest.isLoading ? (
              <Box sx={{ p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : loadRequest.isError ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="error">
                  {error ?? t('exceptions.SHARE_TOKEN_NOT_FOUND')}
                </Typography>
              </Box>
            ) : messages.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('sharedChat.empty')}
                </Typography>
              </Box>
            ) : (
              <MessagesListComponent>
                {messages.map((message) => (
                  <MessagesListItemComponent
                    key={message.id}
                    message={message}
                    downloadFile={downloadFile}
                  />
                ))}
              </MessagesListComponent>
            )}
          </ContainerComponent>
        </Box>
      </ScrollContainerContext.Provider>
    </Box>
  );
}
